import React from 'react';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Atualiza o state para que a próxima renderização mostre a UI alternativa
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Você também pode registrar o erro em um serviço de relatórios de erros
    console.error("Erro capturado pelo ErrorBoundary:", error, errorInfo);
    this.setState({ errorInfo });
    
    // Aqui você poderia enviar o erro para um serviço como Sentry, LogRocket, etc.
    // if (process.env.NODE_ENV === 'production') {
    //   // enviarErroParaServico(error, errorInfo);
    // }
  }

  render() {
    if (this.state.hasError) {
      // Você pode renderizar qualquer UI alternativa
      return this.props.fallback ? 
        this.props.fallback(this.state.error, this.state.errorInfo, this.resetErrorBoundary) : 
        <ErrorFallback 
          error={this.state.error} 
          resetErrorBoundary={() => {
            this.setState({ hasError: false, error: null, errorInfo: null });
            if (this.props.onReset) {
              this.props.onReset();
            }
          }} 
        />;
    }

    return this.props.children;
  }
}

// Componente funcional para exibição de erro
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper 
        elevation={3}
        sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.9)',
          borderRadius: 2
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        
        <Typography variant="h4" component="h1" gutterBottom textAlign="center" fontWeight="bold">
          Ops! Algo deu errado
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Encontramos um problema ao carregar esta página. Você pode tentar recarregá-la ou voltar à página inicial.
        </Typography>
        
        {error && process.env.NODE_ENV !== 'production' && (
          <Box 
            sx={{ 
              p: 2, 
              my: 2, 
              backgroundColor: 'rgba(0,0,0,0.05)', 
              borderRadius: 1,
              width: '100%',
              overflowX: 'auto'
            }}
          >
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {error.toString()}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<RefreshIcon />}
            onClick={resetErrorBoundary}
          >
            Tentar Novamente
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
          >
            Voltar para o Início
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

// HOC para envolver componentes com o ErrorBoundary
export const withErrorBoundary = (Component, options = {}) => {
  return (props) => (
    <ErrorBoundaryClass {...options}>
      <Component {...props} />
    </ErrorBoundaryClass>
  );
};

export default ErrorBoundaryClass;
